import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import lodashGet from 'lodash/fp/get';
import lodashCompact from 'lodash/fp/compact';

import typeCheck from 'common/util/typeCheck';
import {
  findHouseholdApplication,
  findHouseholdApplicationSubmission,
} from 'common/householdApplication/applicationHelpers';
import {
  isListingTenantScreening,
} from 'common/listingHelpers';

import {
  HOUSEHOLD_APPLICATION_CATEGORY_HOUSEHOLD_APPLICATION,
  HOUSEHOLD_APPLICATION_CATEGORY_GUARANTOR_APPLICATION,
} from 'common/householdConstants';
import { SR_API_ACTION_GET_LISTING_BY_ID } from 'api/actionTypes';
import {
  USER_ROLE_RENTER,
  hasRoleAdmin,
} from 'common/userRoles';
import {
  RENTAL_APPLICATION_STEP_REQUIREMENTS,
  RENTAL_APPLICATION_STEP_PERSONAL_INFO,
  RENTAL_APPLICATION_STEP_PAY,
  RENTAL_APPLICATION_STEP_FORM,
  getRentalApplicationUrl,
  getRedirectRentalApplicationUrl,
} from 'common/householdApplication/rentalApplicationHelpers';
import {
  SCREEN_ID_RENTAL_APPLICATION_AGREEMENT,
  SCREEN_ID_RENTAL_APPLICATION_CREDIT_INFO,
  SCREEN_ID_RENTAL_APPLICATION_PAYMENT,
  SCREEN_ID_RENTAL_APPLICATION_FORM,
} from 'client/constants/screenIds';

import ActivityIndicator from 'client/components/ActivityIndicator';
import SrRentalApplicationFlowStepHeader from 'client/components/SrRentalApplicationFlowStepHeader';
import SrRentalApplicationFlowStepAgreement from 'client/components/SrRentalApplicationFlowStepAgreement';
import SrRentalApplicationFlowStepPersonalInfo from 'client/components/SrRentalApplicationFlowStepPersonalInfo';
import SrRentalApplicationFlowStepPay from 'client/components/SrRentalApplicationFlowStepPay';
import SrRentalApplicationFlowStepForm from 'client/components/SrRentalApplicationFlowStepForm';

import branch from 'client/core/branch';
import withApiState from 'client/helpers/withApiState';
import withTransitionHook from 'client/helpers/withTransitionHook';
import allowedRoles from 'client/helpers/allowedRoles';
import withScreenId from 'client/helpers/withScreenId';
import UserActions from 'client/actions/User';
import PartnerActions from 'client/actions/Partner';
import ConcessionAction from 'client/actions/Concession';
import TenantScreeningCriteriaActions from 'client/actions/TenantScreeningCriteria';
import ListingActions from 'client/actions/Listing';
import HouseholdActions from 'client/actions/Household';
import HouseholdApplicationActions from 'client/actions/HouseholdApplication';
import HouseholdListingActions from 'client/actions/HouseholdListing';
import IdVerificationsActions from 'client/actions/IdVerifications';

const RENTAL_APPLICATION_STEP_ID_TO_COMPONENT_MAPPING = {
  [RENTAL_APPLICATION_STEP_REQUIREMENTS]: SrRentalApplicationFlowStepAgreement,
  [RENTAL_APPLICATION_STEP_PERSONAL_INFO]: SrRentalApplicationFlowStepPersonalInfo,
  [RENTAL_APPLICATION_STEP_PAY]: SrRentalApplicationFlowStepPay,
  [RENTAL_APPLICATION_STEP_FORM]: SrRentalApplicationFlowStepForm,
};

const RENTAL_APPLICATION_STEP_ID_TO_SCREEN_MAPPING = {
  [RENTAL_APPLICATION_STEP_REQUIREMENTS]: SCREEN_ID_RENTAL_APPLICATION_AGREEMENT,
  [RENTAL_APPLICATION_STEP_PERSONAL_INFO]: SCREEN_ID_RENTAL_APPLICATION_CREDIT_INFO,
  [RENTAL_APPLICATION_STEP_PAY]: SCREEN_ID_RENTAL_APPLICATION_PAYMENT,
  [RENTAL_APPLICATION_STEP_FORM]: SCREEN_ID_RENTAL_APPLICATION_FORM,
};

@withTransitionHook
@allowedRoles({
  roles: [
    USER_ROLE_RENTER,
  ],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId((props) => (
  RENTAL_APPLICATION_STEP_ID_TO_SCREEN_MAPPING[props.match.params.stepId]
))
@withApiState({
  listingApiState: {
    action: SR_API_ACTION_GET_LISTING_BY_ID,
  },
})
@branch(({ match }) => {
  return {
    listing: ['listing'],
    concessions: ['concessions', 'byListingId', match.params.listingId],
    user: ['users', 'byId', match.params.userId],
    currentUser: ['currentUser'],
    household: ['household', 'byUserId', match.params.userId],
    householdApplications: ['householdApplications'],
    householdApplicationSubmissions: ['householdApplicationSubmissions'],
    tenantScreeningCriteriaByPartner: ['tenantScreeningCriteria', 'byPartnerManagementCompany'],
    tenantScreeningCriteriaByListingId: ['tenantScreeningCriteria', 'byListingId'],
    renterProfilesByUserId: ['household', 'renterProfilesByUserId'],
    tenantAlertReportByUserId: ['household', 'tenantAlertReportByUserId'],
    householdListingsBy: ['household', 'householdListingsBy'],
    appUrl: ['env', 'APP_URL'],
  };
})
@branch(({ listing }) => {
  return listing?.partnerId
    ? { partner: ['partners', 'byId', listing.partnerId] }
    : {};
})
class RentalApplicationPage extends Component {
  static fetchData(match, inject) {
    const listingId = parseInt(match.params.listingId, 10);
    const userId = parseInt(match.params.userId, 10);

    return Promise.all([
      ListingActions.getById(inject, listingId),
      UserActions.getTenantAlertReport(inject, { userId }),
      HouseholdActions.getApplications(inject, { userId }),
      HouseholdActions.loadApplicationSubmissions(inject, { userId }),
      TenantScreeningCriteriaActions.getTenantScreeningCriteria(inject, { listingId }),
      HouseholdApplicationActions.getHouseholdApplicationsByUserId(inject, { userId }),
      HouseholdActions.loadRenterProfile(inject, { userId, forceRefresh: true }),
      HouseholdActions.getByUserId(inject, userId),
      UserActions.getById(inject, {
        userId,
        refresh: true,
      }),
      ConcessionAction.getByListingId(inject, {
        listingId: parseInt(match.params.listingId, 10),
        forceReload: true,
      }),
      ListingActions.getHouseholdsApplyingToListing(inject, {
        listingId: listingId,
        forceRefresh: true,
      }),
      IdVerificationsActions.getByUserSession(inject),
    ]).then(([listing]) => {
      typeCheck('listing::NonEmptyObject', listing);
      const household = inject.state.get(['household', 'byUserId', userId]);

      return Promise.all(lodashCompact([
        household && HouseholdListingActions.getBy(inject, {
          listingId: listing.id,
          householdId: household.id,
        }),
        TenantScreeningCriteriaActions.getTenantScreeningCriteria(inject, {
          managementCompanyName: listing.listingManagementCompany,
        }),
        ...(listing.partnerId
          ? PartnerActions.getById(inject, {
            partnerId: listing.partnerId,
          })
          : []),
      ]));
    });
  }

  static getPageMetadata = () => ({
    pageTitle: 'Application | Sunroom Rentals',
  })

  static propTypes = {
    isTransitioning: PropTypes.bool.isRequired,
    listingApiState: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    listing: PropTypes.object,
    user: PropTypes.object,
    household: PropTypes.object,
    householdApplications: PropTypes.array,
    householdApplicationSubmissions: PropTypes.array,
    renterProfilesByUserId: PropTypes.object.isRequired,
    tenantScreeningCriteriaByPartner: PropTypes.object.isRequired,
    tenantScreeningCriteriaByListingId: PropTypes.object.isRequired,
    tenantAlertReportByUserId: PropTypes.object.isRequired,
    appUrl: PropTypes.string.isRequired,
    partner: PropTypes.object,
    householdListingsBy: PropTypes.object,
  };

  render() {
    const {
      isTransitioning,
      listingApiState,
      dispatch,
      history,
      match,
      listing,
      concessions,
      household,
      user,
      tenantScreeningCriteriaByPartner,
      tenantScreeningCriteriaByListingId,
      tenantAlertReportByUserId,
      householdApplications,
      householdApplicationSubmissions,
      renterProfilesByUserId,
      appUrl,
      partner,
      householdListingsBy,
      currentUser,
    } = this.props;

    const {
      stepId,
    } = match.params;

    if (listingApiState.inProgress || isTransitioning) {
      return (
        <div className='RentalApplicationPage'>
          <SrRentalApplicationFlowStepHeader activeStep={0} />
          <div className='main'>
            <ActivityIndicator />
          </div>
        </div>
      );
    }

    const RentalApplicationStep = RENTAL_APPLICATION_STEP_ID_TO_COMPONENT_MAPPING[stepId];
    if (!listing || !user || !RentalApplicationStep || !Array.isArray(householdApplications)) {
      return <Redirect to={getRedirectRentalApplicationUrl()} />;
    }

    const isTenantScreening = isListingTenantScreening(listing);
    const tenantScreeningCriteria = (
      tenantScreeningCriteriaByListingId[listing.id]
      || tenantScreeningCriteriaByPartner[listing.listingManagementCompany]
    );
    const tenantAlertReport = tenantAlertReportByUserId[user.id];
    const renterProfile = renterProfilesByUserId[user.id];
    const householdListing = household ? householdListingsBy[`${household.id}-${listing.id}`] : null;
    const householdApplication = findHouseholdApplication({
      householdApplications,
      category: HOUSEHOLD_APPLICATION_CATEGORY_HOUSEHOLD_APPLICATION,
      listingId: listing.id,
    });
    const guarantorApplication = findHouseholdApplication({
      householdApplications,
      category: HOUSEHOLD_APPLICATION_CATEGORY_GUARANTOR_APPLICATION,
    });

    // eslint-disable-next-line max-len
    const householdApplicationSubmission = guarantorApplication && householdApplicationSubmissions && findHouseholdApplicationSubmission({
      applicationSubmissions: householdApplicationSubmissions,
      applicationId: guarantorApplication.id,
      listingId: listing.id,
    });

    // NOTE: We need a household application or a household application submission for a Sunroom listing
    // with a tenant screening criteria to go through the rental application flow.
    if (!(householdApplication || householdApplicationSubmission) || !tenantScreeningCriteria) {
      return <Redirect to={getRedirectRentalApplicationUrl()} />;
    }
    const getCurrentRentalApplicationUrl = (rentalApplicationStepId) => (
      getRentalApplicationUrl({
        userId: user.id,
        listingId: listing.id,
        rentalApplicationStepId: rentalApplicationStepId,
      })
    );
    const goToNextRentalApplicationStep = (rentalApplicationStepId) => {
      history.push(getCurrentRentalApplicationUrl(rentalApplicationStepId));
    };
    const exitRentalApplication = () => {
      if (currentUser.id !== user.id && hasRoleAdmin(currentUser.roles)) {
        history.push(`/admin/household/all/${household.leadRenterId}`);
      }
      else {
        history.push(getRedirectRentalApplicationUrl());
      }
    };

    return (
      <RentalApplicationStep
        dispatch={dispatch}
        listing={listing}
        concessions={concessions}
        partner={partner}
        user={user}
        renterProfile={renterProfile}
        isTenantScreening={isTenantScreening}
        tenantAlertReport={tenantAlertReport}
        householdListing={householdListing}
        tenantScreeningCriteria={tenantScreeningCriteria}
        householdApplicationId={householdApplication && householdApplication.id}
        guarantorApplicationId={guarantorApplication && guarantorApplication.id}
        householdApplicationSubmissionId={householdApplicationSubmission && householdApplicationSubmission.id}
        agreeRenterRequirements={
          lodashGet('agreeRenterRequirements', householdApplication || householdApplicationSubmission)
        }
        householdId={
          lodashGet('householdId', householdApplication || householdApplicationSubmission)
        }
        stripeChargeId={
          lodashGet('stripeChargeId', householdApplicationSubmission)
        }
        household={household}
        getCurrentRentalApplicationUrl={getCurrentRentalApplicationUrl}
        goToNextRentalApplicationStep={goToNextRentalApplicationStep}
        exitRentalApplication={exitRentalApplication}
        appUrl={appUrl}
      />
    );
  }
}

RentalApplicationPage.displayName = 'RentalApplicationPage';

export default RentalApplicationPage;
